package com.klantenstop.web.utils

// Manual diacritic-stripping map covering Dutch/French/German characters likely to appear in
// Belgian city and category names. There's no JVM-only java.text.Normalizer available on
// Kotlin/JS, so this mirrors (without reusing) the backend's CuratedCity slug canonicalization.
private val DIACRITIC_MAP: Map<Char, String> = mapOf(
    'à' to "a", 'á' to "a", 'â' to "a", 'ã' to "a", 'ä' to "a", 'å' to "a",
    'À' to "a", 'Á' to "a", 'Â' to "a", 'Ã' to "a", 'Ä' to "a", 'Å' to "a",
    'ç' to "c", 'Ç' to "c",
    'è' to "e", 'é' to "e", 'ê' to "e", 'ë' to "e",
    'È' to "e", 'É' to "e", 'Ê' to "e", 'Ë' to "e",
    'ì' to "i", 'í' to "i", 'î' to "i", 'ï' to "i",
    'Ì' to "i", 'Í' to "i", 'Î' to "i", 'Ï' to "i",
    'ñ' to "n", 'Ñ' to "n",
    'ò' to "o", 'ó' to "o", 'ô' to "o", 'õ' to "o", 'ö' to "o",
    'Ò' to "o", 'Ó' to "o", 'Ô' to "o", 'Õ' to "o", 'Ö' to "o",
    'ù' to "u", 'ú' to "u", 'û' to "u", 'ü' to "u",
    'Ù' to "u", 'Ú' to "u", 'Û' to "u", 'Ü' to "u",
    'ý' to "y", 'ÿ' to "y", 'Ý' to "y",
    'ß' to "ss",
)

private val NON_SLUG_CHARACTER = Regex("[^a-z0-9\\s-]+")
private val HYPHEN_RUN = Regex("-+")
private val WHITESPACE_RUN = Regex("\\s+")

/**
 * Converts this string into a URL-safe slug: strips diacritics, lowercases, joins separate words
 * with "+" (e.g. "Sint Niklaas" -> "sint+niklaas"), and keeps "-" as a literal, preserved character
 * so genuinely hyphenated names (e.g. "Houthalen-Helchteren") survive untouched. Any other stray
 * punctuation is collapsed into a single hyphen. Leading/trailing "-"/"+" are trimmed.
 */
fun String.toUrlSlug(): String {
    val withoutDiacritics = this.map { DIACRITIC_MAP[it] ?: it.toString() }.joinToString("")
    return withoutDiacritics
        .lowercase()
        .replace(NON_SLUG_CHARACTER, "-")
        .replace(HYPHEN_RUN, "-")
        .replace(WHITESPACE_RUN, "+")
        .trim('-', '+')
}

/**
 * Converts a URL slug (as produced by [toUrlSlug]) back into a human-readable display string, e.g.
 * "sint+niklaas" -> "Sint Niklaas". "-" is treated as a literal character of a word (e.g.
 * "houthalen-helchteren" -> "Houthalen-Helchteren") rather than as a space, since [toUrlSlug] only
 * ever uses "+" to represent a space. This is a best-effort reversal only: diacritics, original
 * casing, and whether a "-" was a literal hyphen or a stray-punctuation placeholder are lost during
 * slugification and cannot be recovered.
 */
fun String.fromUrlSlugDisplay(): String =
    trim('-', '+')
        .replace('+', ' ')
        .split(' ')
        .filter { it.isNotBlank() }
        .joinToString(" ") { word ->
            if (word.all { it == '-' }) {
                word
            } else {
                word.split('-')
                    .filter { it.isNotBlank() }
                    .joinToString("-") { part -> part.replaceFirstChar { it.uppercase() } }
            }
        }
